// File: frontend/src/components/AgentRoute.js
/**
 * Protected route wrapper for agent portal.
 * Redirects to /agent/login if no agent token exists.
 */
import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAgent } from "../context/AgentContext";
import { Box, CircularProgress } from "@mui/material";
import { DARK } from "../theme";

// Agent portal is a dark-only surface — DARK design tokens (frontend/src/theme.js)
// are its single source of truth, matching AgentLogin / AgentLayout.
const P = DARK;

export default function AgentRoute({ children }) {
  const { token, loading } = useAgent();
  const location = useLocation();

  if (loading) {
    return (
      <Box sx={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", bgcolor:P.bg.page }}>
        <CircularProgress sx={{ color:P.info.main }} />
      </Box>
    );
  }

  if (!token) {
    return <Navigate to="/agent/login" state={{ from: location }} replace />;
  }

  return children;
}


// ─────────────────────────────────────────────────────────────────────────────
// HOW TO WIRE INTO YOUR App.jsx / Router
// ─────────────────────────────────────────────────────────────────────────────
//
// 1. Wrap your entire app (or router) with <AgentProvider>:
//
//   import { AgentProvider } from "./context/AgentContext";
//
//   <AgentProvider>
//     <RouterProvider router={router} />   // or <BrowserRouter>…
//   </AgentProvider>
//
//
// 2. Add these routes to your router config (React Router v6):
//
//   import AgentLogin    from "./pages/AgentLogin";
//   import AgentLayout   from "./components/AgentLayout";
//   import AgentRoute    from "./components/AgentRoute";
//   import AgentDashboard   from "./pages/agent/AgentDashboard";
//   import AgentVouchers    from "./pages/agent/AgentVouchers";
//   import AgentSell        from "./pages/agent/AgentSell";
//   import AgentCommissions from "./pages/agent/AgentCommissions";
//   import AgentProfile     from "./pages/agent/AgentProfile";
//
//   {
//     path: "/agent/login",
//     element: <AgentLogin />,
//   },
//   {
//     path: "/agent",
//     element: (
//       <AgentRoute>
//         <AgentLayout />
//       </AgentRoute>
//     ),
//     children: [
//       { index: true,              element: <Navigate to="/agent/dashboard" replace /> },
//       { path: "dashboard",        element: <AgentDashboard />   },
//       { path: "vouchers",         element: <AgentVouchers />    },
//       { path: "sell",             element: <AgentSell />        },
//       { path: "commissions",      element: <AgentCommissions /> },
//       { path: "profile",          element: <AgentProfile />     },
//     ],
//   },
//
//
// 3. Register the backend routes in your Express app (app.js / server.js):
//
//   const agentAuthRoutes = require("./routes/agent-auth");
//   const agentsRoutes    = require("./routes/agents");
//
//   app.use("/api/agent-auth", agentAuthRoutes);
//   app.use("/api/agents",     agentsRoutes);
//
//   // Note: /api/agents already existed — just replace the old file.
//   // agent-auth.js is new.
//
//
// 4. Environment variables needed (backend .env):
//
//   AGENT_JWT_SECRET=your_very_long_random_secret_here
//   # Falls back to: process.env.JWT_SECRET + "_agent"
//
//
// 5. Install bcryptjs if not already installed:
//
//   npm install bcryptjs
//
//
// 6. Database: all schema changes are auto-applied by ensureSchema()
//    on first request — no manual migrations needed.
//
// ─────────────────────────────────────────────────────────────────────────────
